import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import type { SreInvestigateTarget } from '../types';
import {
  getSreInvestigationMode,
  isInvestigatableState,
} from '../constants';
import { useSreInvestigationStream } from '../hooks/useSreInvestigationStream';
import {
  buildMarkdownFromReport,
  describeInvestigationMode,
  postInvestigationWorkNote,
} from '../services/sreInvestigationService';
import { ensureSreFontsLoaded } from '../styles/theme';
import '../styles/sre-investigation.css';
import InvestigationReport from './InvestigationReport';
import ExecutionTrace from './ExecutionTrace';
import InvestigationRunStats from './InvestigationRunStats';
import SreWorkbenchBlur from './SreWorkbenchBlur';

export interface SreInvestigatePanelProps {
  open: boolean;
  onClose: () => void;
  target: SreInvestigateTarget | null;
  onPosted?: () => void;
  autoStart?: boolean;
  /** When set, load this persisted run instead of showing empty / auto-start. */
  initialRunId?: string | null;
  /**
   * Trigger source for new runs (T-033).
   * Pass `reinvestigate` to bypass evaluate cooldown and audit operator re-runs.
   */
  triggerSource?: 'pulse_ui' | 'reinvestigate' | string;
}

/**
 * Pulse SRE Investigation workbench panel — structured, Pulse-branded, distinct from OpenSRE UI.
 */
const SreInvestigatePanel: React.FC<SreInvestigatePanelProps> = ({
  open,
  onClose,
  target,
  onPosted,
  autoStart = true,
  initialRunId = null,
  triggerSource: startTriggerSource = 'pulse_ui',
}) => {
  const mode = getSreInvestigationMode();
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const startedForOpenRef = React.useRef<string | null>(null);
  const hydratedForOpenRef = React.useRef<string | null>(null);

  useEffect(() => {
    ensureSreFontsLoaded();
  }, []);

  const {
    runId,
    status,
    isStreaming,
    error,
    toolCalls,
    thoughts,
    report,
    conclusionMarkdown,
    startedAt,
    completedAt,
    triggerSource,
    startInvestigation,
    hydrateFromStoredRun,
    cancel,
    reset,
  } = useSreInvestigationStream({
    onError: (message) => toast.error(message),
  });

  const triggeredBy = React.useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const parsed = JSON.parse(raw) as { email?: string; firstName?: string };
        if (parsed.email) return parsed.email;
        if (parsed.firstName) return parsed.firstName;
      }
    } catch {
      /* ignore */
    }
    return triggerSource;
  }, [triggerSource]);

  const ticketNumber = target?.ticketNumber?.trim() || '';
  const canInvestigate = Boolean(ticketNumber) && isInvestigatableState(target?.ticketState);

  const handleStart = useCallback(() => {
    if (!target?.ticketNumber) return;
    setPosted(false);
    void startInvestigation({
      ticketNumber: target.ticketNumber,
      entityType: target.entityType,
      entityId: target.entityId,
      alertId: target.alertId,
      engagementId: target.engagementId,
      title: target.title,
      description: target.description,
      node: target.node || target.computer,
      computer: target.computer || target.node,
      resourceId: target.resourceId,
      resourceGroup: target.resourceGroup,
      vmName: target.vmName,
      source: target.source || undefined,
      triggerSource: startTriggerSource || 'pulse_ui',
      prompt: [
        `Investigate Pulse ${target.entityType || 'ticket'} ${target.ticketNumber}`,
        target.title,
        target.description,
        target.node || target.computer
          ? `Host/node: ${target.node || target.computer}`
          : '',
        target.resourceId ? `Resource: ${target.resourceId}` : '',
      ]
        .filter(Boolean)
        .join('\n'),
    });
  }, [startInvestigation, target, startTriggerSource]);

  const handleReinvestigate = useCallback(() => {
    if (!target?.ticketNumber) return;
    setPosted(false);
    void startInvestigation({
      ticketNumber: target.ticketNumber,
      entityType: target.entityType,
      entityId: target.entityId,
      alertId: target.alertId,
      engagementId: target.engagementId,
      title: target.title,
      description: target.description,
      node: target.node || target.computer,
      computer: target.computer || target.node,
      resourceId: target.resourceId,
      resourceGroup: target.resourceGroup,
      vmName: target.vmName,
      source: target.source || undefined,
      triggerSource: 'reinvestigate',
      prompt: [
        `Reinvestigate Pulse ${target.entityType || 'ticket'} ${target.ticketNumber}`,
        target.title,
        target.description,
        target.node || target.computer
          ? `Host/node: ${target.node || target.computer}`
          : '',
        target.resourceId ? `Resource: ${target.resourceId}` : '',
      ]
        .filter(Boolean)
        .join('\n'),
    });
  }, [startInvestigation, target]);

  useEffect(() => {
    if (!open) {
      startedForOpenRef.current = null;
      hydratedForOpenRef.current = null;
      return;
    }

    const hydrateId = initialRunId?.trim() || '';
    if (hydrateId) {
      const key = `hydrate:${hydrateId}`;
      if (hydratedForOpenRef.current === key) return;
      hydratedForOpenRef.current = key;
      startedForOpenRef.current = key;
      setPosted(false);
      void hydrateFromStoredRun(hydrateId);
      return;
    }

    // Fresh open — clear any previously hydrated run
    const freshKey = `fresh:${ticketNumber}:${target?.entityId || ''}:${startTriggerSource}`;
    if (hydratedForOpenRef.current !== freshKey) {
      hydratedForOpenRef.current = freshKey;
      setPosted(false);
      reset();
    }

    if (!autoStart || !canInvestigate || !ticketNumber) return;
    if (startedForOpenRef.current === freshKey) return;
    startedForOpenRef.current = freshKey;
    handleStart();
  }, [
    open,
    autoStart,
    canInvestigate,
    ticketNumber,
    target?.entityId,
    initialRunId,
    startTriggerSource,
    reset,
    handleStart,
    hydrateFromStoredRun,
  ]);

  const handleClose = () => {
    if (isStreaming) cancel();
    onClose();
  };

  const markdown =
    conclusionMarkdown || (report ? buildMarkdownFromReport(report) : '');

  const handlePostWorkNote = async () => {
    if (!target?.entityId || !target.entityType || !markdown) {
      toast.error('Cannot post: missing ticket id or report content');
      return;
    }
    if (target.entityType === 'alert') {
      toast.error('Open the related case/incident to post a work note from Phase A');
      return;
    }

    setPosting(true);
    try {
      const result = await postInvestigationWorkNote({
        entityType: target.entityType,
        entityId: target.entityId,
        markdown,
        runId: runId || undefined,
      });
      if (!result.posted) {
        toast.error(result.error || 'Failed to post work note');
        return;
      }
      setPosted(true);
      toast.success('Investigation report posted to work notes');
      onPosted?.();
    } finally {
      setPosting(false);
    }
  };

  const handleCopy = async () => {
    if (!markdown) return;
    try {
      await navigator.clipboard.writeText(markdown);
      toast.success('Report copied to clipboard');
    } catch {
      toast.error('Unable to copy report');
    }
  };

  const showWorkbench =
    status === 'running' ||
    status === 'completed' ||
    status === 'failed' ||
    status === 'cancelled' ||
    Boolean(startedAt);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Blur stays outside opacity Transition so SSE/timer re-renders cannot drop it */}
        <SreWorkbenchBlur />
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/45 transition-opacity" aria-hidden />
        </Transition.Child>

        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-8 sm:pl-12">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-250"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-180"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="pointer-events-auto flex h-full w-screen max-w-2xl flex-col">
              <div className="sre-wb flex h-full flex-col bg-slate-50 font-['DM_Sans',ui-sans-serif,system-ui,sans-serif] shadow-2xl shadow-slate-900/20">
                    {/* Header */}
                    <header className="relative overflow-hidden border-b border-slate-200 bg-white">
                      <div
                        className="pointer-events-none absolute inset-0 opacity-[0.55]"
                        style={{
                          backgroundImage:
                            'radial-gradient(circle at 12% 20%, rgba(27,78,255,0.12), transparent 42%), radial-gradient(circle at 88% 0%, rgba(201,180,1,0.14), transparent 36%)',
                        }}
                      />
                      <div className="relative flex items-start justify-between gap-3 px-5 py-4 sm:px-6">
                        <div className="min-w-0">
                          <div className="mb-1.5 flex items-center gap-2">
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600 text-white shadow-sm shadow-primary-600/25">
                              <ShieldCheckIcon className="h-5 w-5" />
                            </span>
                            <div>
                              <Dialog.Title className="text-base font-semibold tracking-tight text-slate-900">
                                Investigation workbench
                              </Dialog.Title>
                              <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
                                Pulse SRE
                              </p>
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 font-['IBM_Plex_Mono',ui-monospace,monospace] text-xs font-medium text-slate-700">
                              {ticketNumber || 'No ticket'}
                            </span>
                            {mode === 'demo' && (
                              <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-secondary-800 ring-1 ring-inset ring-secondary-300">
                                Demo
                              </span>
                            )}
                            <span className="text-xs text-slate-400">
                              {describeInvestigationMode()}
                            </span>
                          </div>
                          <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 text-xs leading-5 text-slate-700">
                            <div className="min-w-0">
                              <dt className="inline font-bold text-slate-900">Customer Name:</dt>{' '}
                              <dd className="inline font-normal break-words">
                                {target?.customerName?.trim() || '—'}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="inline font-bold text-slate-900">Engagement Name:</dt>{' '}
                              <dd className="inline font-normal break-words">
                                {target?.engagementName?.trim() || '—'}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="inline font-bold text-slate-900">Source:</dt>{' '}
                              <dd className="inline font-normal break-words">
                                {target?.source?.trim() || '—'}
                              </dd>
                            </div>
                          </dl>
                        </div>
                        <button
                          type="button"
                          className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                          onClick={handleClose}
                          aria-label="Close investigation workbench"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                      {isStreaming && (
                        <div className="sre-wb-progress h-0.5 bg-primary-100" />
                      )}
                    </header>

                    {/* Body */}
                    <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
                      {!canInvestigate && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900">
                          Investigation is limited for the current ticket state
                          {target?.ticketState ? ` (${target.ticketState})` : ''}.
                        </div>
                      )}

                      {error && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-800">
                          {error}
                        </div>
                      )}

                      {showWorkbench && (
                        <InvestigationRunStats
                          status={status}
                          isStreaming={isStreaming}
                          toolCallsCount={toolCalls.length}
                          startedAt={startedAt}
                          completedAt={completedAt}
                          triggeredBy={triggeredBy}
                        />
                      )}

                      {report && (
                        <div className="sre-wb-fade sre-wb-fade-delay-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="h-px flex-1 bg-slate-200" />
                            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                              Findings
                            </span>
                            <span className="h-px flex-1 bg-slate-200" />
                          </div>
                          <InvestigationReport report={report} />
                        </div>
                      )}

                      {(status === 'running' ||
                        status === 'completed' ||
                        status === 'failed' ||
                        status === 'cancelled' ||
                        toolCalls.length > 0 ||
                        thoughts.length > 0) && (
                        <div className="space-y-2">
                          {report && (
                            <div className="flex items-center gap-2">
                              <span className="h-px flex-1 bg-slate-200" />
                              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                Evidence
                              </span>
                              <span className="h-px flex-1 bg-slate-200" />
                            </div>
                          )}
                          <ExecutionTrace
                            toolCalls={toolCalls}
                            thoughts={thoughts}
                            isRunning={isStreaming}
                          />
                        </div>
                      )}

                      {!report && !isStreaming && status === 'idle' && (
                        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-12 text-center">
                          <ShieldCheckIcon className="mx-auto h-10 w-10 text-primary-200" />
                          <p className="mt-3 text-sm font-medium text-slate-700">
                            Ready to investigate
                          </p>
                          <p className="mt-1 text-sm text-slate-500">
                            Run a structured analysis to collect evidence and produce a report.
                          </p>
                          <button
                            type="button"
                            onClick={handleStart}
                            disabled={!canInvestigate}
                            className="mt-5 inline-flex items-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary-600/20 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Start investigation
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <footer className="border-t border-slate-200 bg-white px-5 py-3.5 sm:px-6">
                      <div className="flex flex-wrap items-center gap-2">
                        {isStreaming ? (
                          <button
                            type="button"
                            onClick={cancel}
                            className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              reset();
                              handleReinvestigate();
                            }}
                            disabled={!canInvestigate}
                            title="Start a new investigation (bypasses evaluate cooldown)"
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                          >
                            <ArrowPathIcon className="h-4 w-4" />
                            Reinvestigate
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={handleCopy}
                          disabled={!markdown}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                          Copy
                        </button>

                        <button
                          type="button"
                          onClick={() => void handlePostWorkNote()}
                          disabled={!markdown || posting || posted || !target?.entityId}
                          className="inline-flex items-center rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm shadow-primary-600/20 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {posted ? 'Posted' : posting ? 'Posting…' : 'Post to work notes'}
                        </button>
                      </div>
                    </footer>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SreInvestigatePanel;
